import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Form, 
  Table, 
  Button, 
  InputGroup, 
  Spinner,
  Card,
  Badge,
  Pagination
} from 'react-bootstrap';
import { FaSearch, FaEdit, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

import KassaService from '../services/KassaService';
import KassaKirimService from '../services/KassaKirimService';
import KassaChiqimService from '../services/KassaChiqimService';
import ConfirmModal from './ConfirmModal';
import KassaIncomeModal from '../components/kassa/KassaIncomeModal';
import KassaExpenseModal from '../components/kassa/KassaExpenseModal';
import { formatCurrency } from '../utils/formatters';

const KassaTarix = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [itemToEdit, setItemToEdit] = useState(null);
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [kassas, setKassas] = useState([]);
  const [selectedKassa, setSelectedKassa] = useState('');
  const [selectedKassaName, setSelectedKassaName] = useState('');
  // Get first day of current month in YYYY-MM-DD format
  const getFirstDayOfMonth = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}-01`; // First day of current month
  };

  // Get tomorrow's date in YYYY-MM-DD format
  const getToday = () => {
    const date = new Date();
    date.setDate(date.getDate() + 1); // Add one day to get tomorrow
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`; // Tomorrow's date
  };

  // Format date to YYYY-MM-DD for API
  const formatDateForAPI = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };



  const [filters, setFilters] = useState(() => {
    const firstDay = getFirstDayOfMonth();
    const today = getToday();
    return {
      dateDan: firstDay,
      dateGacha: today,
      page: 1,
      perPage: 10
    };
  });

  // Available per page options
  const perPageOptions = [10, 20, 50, 100];

  // Load kassas on component mount
  useEffect(() => {
    const loadKassas = async () => {
      try {
        const response = await KassaService.getKassaList();
        if (response && response.data) {
          console.log('kassalar', response.data)
          setKassas(response.data);
          // Select first kassa by default if available
          if (response.data.length > 0) {
            setSelectedKassa(response.data[0].id.toString());
            setSelectedKassaName(response.data[0].name);
          }
        }
      } catch (error) {
        console.error('Error loading kassas:', error);
        toast.error('Kassalar yuklanmadi');
      }
    };

    loadKassas();
  }, []);

  // Load data when filters or selected kassa changes
  useEffect(() => {
    if (selectedKassa) {
      loadData();
    }
  }, [selectedKassa, filters]);

  const loadData = async () => {
    if (!selectedKassa) return;
    
    try {
      setLoading(true);
      
      // Format dates for API
      const fromDate = filters.dateDan || getFirstDayOfMonth();
      const toDate = filters.dateGacha || getToday();
      
      const response = await KassaService.getKassaKirimChiqimList(
        selectedKassa,
        fromDate,
        toDate,
        filters.perPage,
        filters.page
      );

      if (response && response.data) {
        console.log('API Response:', response.data);
        setTransactions(response.data.data || []);
        setTotalItems(response.data.total || 0);
        setTotalPages(Math.ceil((response.data.total || 0) / filters.perPage) || 1);
      }
    } catch (error) {
      console.error('Error loading transactions:', error);
      toast.error('Tranzaksiyalar yuklanmadi');
    } finally {
      setLoading(false);
    }
  };

  // Handle page change
  const handlePageChange = (page) => {
    setFilters(prev => ({
      ...prev,
      page: page
    }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
      page: 1 // Reset to first page when filters change
    }));
  };
  
  const handlePerPageChange = (e) => {
    const newPerPage = parseInt(e.target.value, 10);
    setFilters(prev => ({
      ...prev,
      perPage: newPerPage,
      page: 1 // Reset to first page when changing items per page
    }));
  };

  const handleKassaChange = (e) => {
    const selectedId = e.target.value;
    const selected = kassas.find(k => k.id.toString() === selectedId);
    
    if (selected) {
      setSelectedKassa(selected.id.toString());
      setSelectedKassaName(selected.name);
      
      // Reset pagination when changing kassa
      setFilters(prev => ({
        ...prev,
        page: 1
      }));
    }
  };
  
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const handleEdit = (item) => {
    setItemToEdit(item);
    if (item.kassa_kirim_id) {
      setShowIncomeModal(true);
    } else if (item.kassa_chiqim_id) {
      setShowExpenseModal(true);
    }
  };

  const handleUpdateTransaction = async (data) => {
    try {
      setLoading(true);
      
      if (itemToEdit.kassa_kirim_id) {
        // Update Kirim
        await KassaKirimService.updateKassaKirim(
          {
            kassa_id: itemToEdit.kassa_id,
            transport_id: itemToEdit.transport_id,
            izoh: data.izoh,
            summa: data.summa,
            foyda: data.foyda || 0
          },
          itemToEdit.kassa_kirim_id
        );
      } else if (itemToEdit.kassa_chiqim_id) {
        // Update Chiqim
        await KassaChiqimService.updateKassaChiqim(
          {
            kassa_id: itemToEdit.kassa_id,
            izoh: data.izoh,
            summa: data.summa
          },
          itemToEdit.kassa_chiqim_id
        );
      }
      
      toast.success('Tranzaksiya muvaffaqiyatli yangilandi');
      loadData();
    } catch (error) {
      console.error('Error updating transaction:', error);
      const errorMessage = error.response?.data?.message || 'Tranzaksiyani yangilashda xatolik yuz berdi';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      setShowIncomeModal(false);
      setShowExpenseModal(false);
      setItemToEdit(null);
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    
    try {
      setLoading(true);
      
      // Determine if it's Kirim or Chiqim and call the appropriate service
      if (itemToDelete.kassa_kirim_id) {
        // This is a Kirim transaction
        await KassaKirimService.deleteKassaKirim(itemToDelete.kassa_kirim_id);
      } else if (itemToDelete.kassa_chiqim_id) {
        // This is a Chiqim transaction
        await KassaChiqimService.deleteKassaChiqim(itemToDelete.kassa_chiqim_id);
      } else {
        throw new Error('Tranzaksiya turi aniqlanmadi');
      }
      
      toast.success('Tranzaksiya muvaffaqiyatli o\'chirildi');
      loadData();
    } catch (error) {
      console.error('Error deleting transaction:', error);
      const errorMessage = error.response?.data?.message || 'Tranzaksiyani o\'chirishda xatolik yuz berdi';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      setShowConfirmModal(false);
      setItemToDelete(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'dd.MM.yyyy HH:mm:ss', { locale: ru });
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  // Helper to parse user string (format: "name phone")
  const parseUserInfo = (userString) => {
    if (!userString) return { name: '-', phone: '' };
    const [name, ...phoneParts] = userString.split(' ');
    return {
      name: name || '-',
      phone: phoneParts.join(' ') || ''
    };
  };

  // Helper to parse transport string (format: "name number")
  const parseTransportInfo = (transportString) => {
    if (!transportString) return { name: '-', number: '' };
    // Simple split - might need adjustment based on actual format
    const parts = transportString.split(' ');
    if (parts.length === 1) return { name: parts[0], number: '' };
    
    // Get the last part as number (assuming it's the license plate)
    const number = parts.pop();
    return {
      name: parts.join(' '),
      number: number
    };
  };

  return (
    <Container fluid className="py-4">
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center" style={{ backgroundColor: '#f8f9fa' }}>
          <h5 className="mb-0">Kassa harakati tarixi: <span style={{ color: '#0d6efd', fontWeight: 'bold' }}>{selectedKassaName}</span></h5>
          <div style={{ position: 'relative', minWidth: '300px' }}>
            <Form.Select 
              className="shadow-sm"
              style={{
                width: '100%',
                fontWeight: '500',
                border: '1px solid #0d6efd',
                backgroundColor: 'white',
                padding: '0.5rem 1rem',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)'
              }}
              value={selectedKassa}
              onChange={handleKassaChange}
              disabled={kassas.length === 0}
            >
              {kassas.map(kassa => (
                <option key={kassa.id} value={kassa.id.toString()}>
                  {kassa.name}
                </option>
              ))}
            </Form.Select>
          </div>
        </Card.Header>
        <Card.Body>
          {/* Filters */}
          <Form onSubmit={handleSearch} className="mb-4">
            <Row>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Sanadan</Form.Label>
                  <Form.Control
                    type="date"
                    name="dateDan"
                    value={filters.dateDan}
                    onChange={handleFilterChange}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Sanagacha</Form.Label>
                  <Form.Control
                    type="date"
                    name="dateGacha"
                    value={filters.dateGacha}
                    onChange={handleFilterChange}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>

          {/* Transactions Table */}
          <div className="table-responsive">
            <Table striped bordered hover>
              <thead className="table-dark">
                <tr>
                  <th>#</th>
                  <th>Sana</th>
                  <th>Amaliyot</th>
                  <th>Summa</th>
                  <th>Balans</th>
                  <th>Izoh</th>
                  <th>Transport</th>
                  {/*<th>Foyda</th>*/}
                  <th>F.I.O</th>
                  <th>Amallar</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="10" className="text-center">
                    {!selectedKassa ? (
                      <div>Iltimos, kassani tanlang</div>
                    ) : (
                      <Spinner animation="border" variant="primary" />
                    )}
                  </td>
                  </tr>
                ) : transactions.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center">
                    Ma'lumot topilmadi
                  </td>
                  </tr>
                ) : (
                  transactions.map((item, index) => (
                    <tr key={`${item.id}-${item.kassa_kirim_id || item.kassa_chiqim_id}`}>
                      <td>{(filters.page - 1) * filters.perPage + index + 1}</td>
                      <td>{formatDate(item.created_at)}</td>
                      <td>
                        {item.kassa_kirim_id ? (
                          <Badge bg="success">Kirim</Badge>
                        ) : (
                          <Badge bg="danger">Chiqim</Badge>
                        )}
                      </td>
                      <td className={item.kassa_kirim_id ? 'text-success fw-bold' : 'text-danger fw-bold'}>
                        {item.kassa_kirim_id ? '+' : '-'} {formatCurrency(Math.abs(item.summa))}
                      </td>
                      <td className="fw-bold">
                        {formatCurrency(item.balance)}
                      </td>
                      <td>{item.izoh || '-'}</td>
                      <td>
                        {item.transport ? (
                          <>
                            {parseTransportInfo(item.transport).name} 
                            {parseTransportInfo(item.transport).number && `(${parseTransportInfo(item.transport).number})`}
                          </>
                        ) : (
                          '-'
                        )}
                      </td>
                      {/*<td className="text-success">*/}
                      {/*  {item.foyda ? formatCurrency(item.foyda) : '0'}*/}
                      {/*</td>*/}
                      <td>
                        {item.user ? (
                          <>
                            {parseUserInfo(item.user).name}
                            {parseUserInfo(item.user).phone && ` (${parseUserInfo(item.user).phone})`}
                          </>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>
                        <div className="d-flex">
                          <Button 
                            variant="link" 
                            size="sm"
                            className="text-primary p-0 me-2"
                            title="Tahrirlash"
                            onClick={() => handleEdit(item)}
                          >
                            <FaEdit />
                          </Button>
                          <Button 
                            variant="link" 
                            size="sm"
                            className="text-danger p-0"
                            title="O'chirish"
                            onClick={() => {
                              setItemToDelete(item);
                              setShowConfirmModal(true);
                            }}
                          >
                            <FaTrash />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          {/* Pagination and Per Page Selector */}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="d-flex align-items-center">
              <span className="me-2">Har sahifada:</span>
              <Form.Select 
                style={{ width: '80px' }} 
                value={filters.perPage}
                onChange={handlePerPageChange}
                disabled={loading}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </Form.Select>
              <span className="ms-2">
                {loading ? 'Yuklanmoqda...' : `Jami: ${totalItems} ta`}
              </span>
            </div>

            <Pagination className="mb-0">
              <Pagination.First
                  onClick={() => handlePageChange(1)}
                  disabled={filters.page === 1 || loading}
              />
              <Pagination.Prev
                  onClick={() => handlePageChange(filters.page - 1)}
                  disabled={filters.page === 1 || loading}
              />

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (filters.page <= 3) {
                  pageNum = i + 1;
                } else if (filters.page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = filters.page - 2 + i;
                }

                return (
                    <Pagination.Item
                        key={pageNum}
                        active={pageNum === filters.page}
                        onClick={() => handlePageChange(pageNum)}
                        disabled={loading}
                    >
                      {pageNum}
                    </Pagination.Item>
                );
              })}

              <Pagination.Next
                  onClick={() => handlePageChange(filters.page + 1)}
                  disabled={filters.page === totalPages || loading}
              />
              <Pagination.Last
                  onClick={() => handlePageChange(totalPages)}
                  disabled={filters.page === totalPages || loading}
              />
            </Pagination>
            {/*{totalPages > 1 && (*/}
            {/*  <nav>*/}
            {/*    <ul className="pagination mb-0">*/}
            {/*      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>*/}
            {/*        <button */}
            {/*          className="page-link" */}
            {/*          onClick={() => handlePageChange(1)}*/}
            {/*          disabled={currentPage === 1 || loading}*/}
            {/*        >*/}
            {/*          &laquo;*/}
            {/*        </button>*/}
            {/*      </li>*/}
            {/*      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (*/}
            {/*        <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>*/}
            {/*          <button */}
            {/*            className="page-link" */}
            {/*            onClick={() => handlePageChange(page)}*/}
            {/*            disabled={loading}*/}
            {/*          >*/}
            {/*            {page}*/}
            {/*          </button>*/}
            {/*        </li>*/}
            {/*      ))}*/}
            {/*      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>*/}
            {/*        <button */}
            {/*          className="page-link" */}
            {/*          onClick={() => handlePageChange(totalPages)}*/}
            {/*          disabled={currentPage === totalPages || loading}*/}
            {/*        >*/}
            {/*          &raquo;*/}
            {/*        </button>*/}
            {/*      </li>*/}
            {/*    </ul>*/}
            {/*  </nav>*/}

            {/*)}*/}
          </div>
        </Card.Body>
      </Card>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        show={showConfirmModal}
        onHide={() => {
          setShowConfirmModal(false);
          setItemToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Tranzaksiyani o'chirish"
        message={
          <div>
            <p>Quyidagi tranzaksiyani o'chirishni istaysizmi?</p>
            <div className="mt-2">
              <p><strong>Sana:</strong> {itemToDelete ? formatDate(itemToDelete.created_at) : ''}</p>
              <p><strong>Turi:</strong> {itemToDelete ? (itemToDelete.kassa_kirim_id ? 'Kirim' : 'Chiqim') : ''}</p>
              <p><strong>Summa:</strong> {itemToDelete ? `${formatCurrency(Math.abs(itemToDelete.summa))} ${itemToDelete.kassa_kirim_id ? '+' : '-'}` : ''}</p>
              <p><strong>Izoh:</strong> {itemToDelete?.izoh || 'Mavjud emas'}</p>
            </div>
          </div>
        }
        confirmText="O'chirish"
        cancelText="Bekor qilish"
      />

      {/* Edit Modals */}
      {itemToEdit && (
        <>
          <KassaIncomeModal
            show={showIncomeModal}
            onHide={() => {
              setShowIncomeModal(false);
              setItemToEdit(null);
            }}
            editing={true}
            refreshData={loadData}
            initialData={{
              id: itemToEdit.kassa_kirim_id,
              kassa_id: itemToEdit.kassa_id,
              transport_id: itemToEdit.transport_id,
              summa: itemToEdit.summa,
              izoh: itemToEdit.izoh || '',
              foyda: itemToEdit.foyda || 0
            }}
          />
          
          <KassaExpenseModal
            show={showExpenseModal}
            onHide={() => {
              setShowExpenseModal(false);
              setItemToEdit(null);
            }}
            editing={true}
            refreshData={loadData}
            initialData={{
              id: itemToEdit.kassa_chiqim_id,
              transport_id: itemToEdit.transport_id || '',
              kassa_id: itemToEdit.kassa_id,
              summa: itemToEdit.summa,
              izoh: itemToEdit.izoh || ''
            }}
          />
        </>
      )}

      {/*/!* Edit Modals *!/*/}
      {/*{itemToEdit && (*/}
      {/*  <>*/}
      {/*    <KassaIncomeModal*/}
      {/*      show={showIncomeModal}*/}
      {/*      onHide={() => {*/}
      {/*        setShowIncomeModal(false);*/}
      {/*        setItemToEdit(null);*/}
      {/*      }}*/}
      {/*      editing={true}*/}
      {/*      initialData={{*/}
      {/*        id: itemToEdit.kassa_kirim_id,*/}
      {/*        kassa_id: itemToEdit.kassa_id,*/}
      {/*        transport_id: itemToEdit.transport_id,*/}
      {/*        summa: itemToEdit.summa,*/}
      {/*        izoh: itemToEdit.izoh || '',*/}
      {/*        foyda: itemToEdit.foyda || 0*/}
      {/*      }}*/}
      {/*    />*/}
      {/*    */}
      {/*    <KassaExpenseModal*/}
      {/*      show={showExpenseModal}*/}
      {/*      onHide={() => {*/}
      {/*        setShowExpenseModal(false);*/}
      {/*        setItemToEdit(null);*/}
      {/*      }}*/}
      {/*      editing={true}*/}
      {/*      initialData={{*/}
      {/*        id: itemToEdit.kassa_chiqim_id,*/}
      {/*        kassa_id: itemToEdit.kassa_id,*/}
      {/*        summa: itemToEdit.summa,*/}
      {/*        izoh: itemToEdit.izoh || ''*/}
      {/*      }}*/}
      {/*    />*/}
      {/*  </>*/}
      {/*)}*/}
    </Container>
  );
};

export default KassaTarix;
